import i18n from "@i18n";
import { setGeoLocation } from "@utils/redux/slices/geoLocation";
import { setRouteId } from "@utils/redux/slices/routeId";
import getMeristemDefaultValue from "@utils/meristemContext/meristemContextDefaults";
import getContext, {
  FetchInAppServerContextError,
  setCookiesFromMeristemContext,
} from "./context";
import { getEmailAndNameFromURLParams } from "./urlParams";
import { USER_IS_BOT } from "./botDetector";
import { setUserIdInSentryScope } from "./sentry";

import store, { observeStore } from "./redux/store";
import { updateRecommendedPlan } from "./redux/slices/recommendedPlan";
import { updateVisitorStatusState } from "./redux/slices/visitorStatus";
import { updatePlans } from "./redux/slices/plans";
import { updateServerContext } from "./redux/slices/serverContext";
import {
  updateUserData,
  initializeNewUser,
  fetchAndInitializeExistingUser,
} from "./redux/slices/userData";
import { updateTrialFromServerContext } from "./redux/slices/trial";
import { setLanguage } from "./redux/slices/language";
import { setDeviceInformation } from "./redux/slices/deviceInformation";
import { updateStartEvents } from "./redux/slices/startEvents";

import EventTracker, {
  setInitialPeopleProperties,
  updateUserContext,
  trackGenericEvent,
  formatMeristemExperimentStateVisitData,
} from "./api/tracker";

import ConversionTracker from "./services/ConversionTracker";
import OptimizelyExperiment from "./services/OptimizelySXExperiment";
import visitTracker from "./services/VisitTracker";

/**
 * Returns the meristemContext passed by buyflow-worker, or it fallbacks to default for development environment
 */
export function getMeristemContext() {
  const meristemContext =
    window?.meristemContext?.context || getMeristemDefaultValue();
  setCookiesFromMeristemContext(meristemContext);
  return meristemContext;
}

/**
 * Update UserData slice (and possibly fetch an existing user's UserData).
 * @param {*} serverContext
 * @param {*} meristemContext
 */
async function updateUserDataSlice(serverContext, meristemContext) {
  // Fetch user from backend if they are in the database.
  // Guard against bots, don't attempt to fetch userData if we detect a bot.
  if (serverContext.user_in_db !== false && !USER_IS_BOT) {
    await store.dispatch(
      fetchAndInitializeExistingUser(serverContext.user_id, meristemContext)
    );
  } else {
    await store.dispatch(initializeNewUser(serverContext, meristemContext));
  }
  const emailAndName = getEmailAndNameFromURLParams();
  if (Object.keys(emailAndName).length !== 0)
    await store.dispatch(updateUserData(emailAndName));
}

/**
 * Track changes to the user's visitor status to mixpanel
 *
 * 1. Add tracking observer to the visitorStatus slice in Redux.
 * 2. Track initial state.
 * @param {*} serverContext
 */
function trackVisitorState(serverContext) {
  const visitorStatusSelector = (state) => state.visitorStatus;
  const trackVisitorStatusToMixPanel = (visitorStatus) => {
    updateUserContext({
      visitorState: visitorStatus.visitorState,
    });
    trackGenericEvent("VisitorStateDetermined", {
      ...visitorStatus,
      context_type: store.getState().serverContext.context_type,
    });
  };
  observeStore(visitorStatusSelector, trackVisitorStatusToMixPanel);

  // Update visitor status state on initialization to kick off VisitorStateDetermined tracking
  // Only update visitor status state on landing and payment to avoid unnecessary duplicate
  // visitorStateDetermined tracking events on post checkout contexts
  if (
    serverContext.context_type === "landing" ||
    serverContext.context_type === "payment"
  ) {
    store.dispatch(
      updateVisitorStatusState({ user_id: serverContext.user_id })
    );
  }
}

/**
 * After we fetch serverContext, Take the data and update the appropriate
 * redux slices with it.
 * @param {*} serverContext
 */
export function updateStoreWithServerContext(serverContext) {
  store.dispatch(updateServerContext(serverContext));
  store.dispatch(updateTrialFromServerContext(serverContext));
  if (serverContext?.plans) store.dispatch(updatePlans(serverContext.plans));
  if (serverContext?.plan)
    store.dispatch(updateRecommendedPlan(serverContext.plan));
}

/**
 * Initialize store with data from meristemContext
 * If the meristemContext window global variable is not available, the slices will fallback to default data
 */
function initializeStoreWithMeristemContextData(meristemContextData) {
  store.dispatch(setDeviceInformation(meristemContextData));
  store.dispatch(setGeoLocation(meristemContextData));
  store.dispatch(setLanguage(meristemContextData.language_code));
  store.dispatch(setRouteId(meristemContextData.route_id));
}

/**
 * Initialize store with data from meristemState
 */
function initializeStoreWithMeristemStateData(meristemStateData) {
  store.dispatch(updateStartEvents(meristemStateData.experiments));
}

/**
 * Initialize redux, set up services, and fire off tracking calls.
 * @returns bool  App initializes successfully
 */
export default async function initializeApplication() {
  try {
    // 1 - get the meristemContext data
    const meristemContext = getMeristemContext();
    // 2 - get serverContext from Django API
    const serverContext = await getContext();
    // 2a - set window.serverContext to make it easily accessible
    window.serverContext = serverContext;
    // 3 - rehydrate redux state with serverContext
    updateStoreWithServerContext(serverContext);
    // 4 - update store with data from meristemContext
    initializeStoreWithMeristemContextData(meristemContext);
    // 5 - set up Sentry and attach user_id as a property
    setUserIdInSentryScope(serverContext.user_id);
    // 6 - Initialize visitTracker. This must happen before we fire any mixpanel events.
    visitTracker.init(serverContext.session_cookie_domain);
    // 7 - update the UserData slice in redux (also, fetch userData if an existing user)
    await updateUserDataSlice(serverContext, meristemContext);
    // 8 - set up mixpanel tracker
    setInitialPeopleProperties();
    // 9 - maybe send an event that signals the user is allocated to an experiment
    const experimentsState = window?.meristemContext?.experimentsCookie || [];
    experimentsState.forEach((experimentState) => {
      trackGenericEvent("OnMeristemExperimentDecided", {
        experimentName: experimentState.experimentName || null,
        variationName: experimentState.variationName || null,
      });
    });
    // 10 - track a visit
    visitTracker.createOrUpdateVisit(
      formatMeristemExperimentStateVisitData(experimentsState) || {}
    );
    // 11 - set up a redux observer that updates visitor state
    trackVisitorState(serverContext);
    // 12 - set up conversion tracker for pixel tracking
    window.conversionTracker = new ConversionTracker();
    // 13 - set up a helper for SX Optimizely experiments to pass through experiment variables.
    window.osxe = new OptimizelyExperiment();
    // 14 - make redux state available for debugging
    window.reduxState = store.getState;
    // 15a - Update i18n language to match serverContext language
    if (i18n && meristemContext.language_code !== i18n.language) {
      i18n.changeLanguage(meristemContext.language_code);
    }
    // 15b - Set the lang property to the <html> tag.
    document.querySelector("html").lang = meristemContext.language_code;
    // 16 - Set up temporary event tracker shim.
    // TODO(patrick): Delete this
    window.tracker = new EventTracker();
    // 17 - Update store with data from meristemState
    const meristemState = window?.meristemContext?.meristemState || {};
    initializeStoreWithMeristemStateData(meristemState);
    return true;
  } catch (e) {
    console.error("Error encountered while initializing application: ", e);
    if (e instanceof FetchInAppServerContextError) {
      window.location = `/identification-error.html${window.location.search}`;
    } else {
      window.location = `/error.html${window.location.search}`;
    }
    return false;
  }
}
